<script setup lang="ts">
import { usePublicSiteRepository } from '~/repositories/publicSiteRepository'

/**
 * Tenant news hub — featured post, Latest / Popular / category tabs,
 * card grid with pagination.
 */
definePageMeta({ layout: false, middleware: ['tenant'] })

const tenant = useTenant()
if (tenant.tenantMode.value === 'platform') {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}
const activeTenant = computed(() => tenant.tenant.value)

const { t } = useI18n()
const repo = usePublicSiteRepository()
const route = useRoute()
const router = useRouter()

const tab = computed(() => (route.query.tab === 'popular' ? 'popular' : 'latest'))
const category = computed(() => (route.query.category as string) || undefined)
const page = computed(() => Number(route.query.page) || 1)

const { data: response } = await useAsyncData(
  'site-news',
  () => repo.getNews({ tab: tab.value, category: category.value, page: page.value }),
  { watch: [tab, category, page] }
)

// featured/categories only arrive on page 1 — keep the last seen values
const categories = ref(response.value?.categories ?? [])
const featured = ref(response.value?.featured ?? null)
watch(response, (r) => {
  if (r?.categories) categories.value = r.categories
  if (r && 'featured' in r) featured.value = r.featured ?? null
})

const posts = computed(() => response.value?.data ?? [])
const meta = computed(() => response.value?.meta)

// The featured post leads the page on the default view; drop it from the
// grid so it doesn't appear twice.
const gridPosts = computed(() =>
  tab.value === 'latest' && !category.value && page.value === 1 && featured.value
    ? posts.value.filter(p => p.slug !== featured.value!.slug)
    : posts.value)

const setQuery = (patch: Record<string, string | undefined>) => {
  router.push({ query: { ...route.query, page: undefined, ...patch } })
}

useSeoMeta({
  title: () => `${t('site.news_title')} — ${activeTenant.value?.name ?? ''}`,
  description: () => `${t('site.news_title')} — ${activeTenant.value?.name ?? ''}`
})
</script>

<template>
  <SiteShell
    v-if="activeTenant"
    :tenant="activeTenant"
  >
    <div class="mx-auto max-w-5xl px-6 py-12 sm:py-16">
      <h1 class="site-display text-4xl tracking-tight sm:text-5xl">
        {{ t('site.news_title') }}
      </h1>

      <!-- Tabs -->
      <div class="mt-8 flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="rounded-[var(--site-radius)] px-3.5 py-1.5 text-sm font-medium transition"
          :class="tab === 'latest' && !category ? 'site-btn-accent' : 'site-btn-outline'"
          @click="setQuery({ tab: undefined, category: undefined })"
        >
          {{ t('site.news_latest') }}
        </button>
        <button
          type="button"
          class="rounded-[var(--site-radius)] px-3.5 py-1.5 text-sm font-medium transition"
          :class="tab === 'popular' ? 'site-btn-accent' : 'site-btn-outline'"
          @click="setQuery({ tab: 'popular', category: undefined })"
        >
          {{ t('site.news_popular') }}
        </button>
        <button
          v-for="cat in categories"
          :key="cat.id"
          type="button"
          class="rounded-[var(--site-radius)] px-3.5 py-1.5 text-sm font-medium transition"
          :class="category === cat.slug ? 'site-btn-accent' : 'site-btn-outline'"
          @click="setQuery({ tab: undefined, category: cat.slug })"
        >
          {{ cat.name }}
        </button>
      </div>

      <!-- Featured -->
      <SiteNewsCard
        v-if="featured && tab === 'latest' && !category && page === 1"
        :post="featured"
        featured
        class="mt-8"
      />

      <!-- Grid -->
      <div
        v-if="gridPosts.length"
        class="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
      >
        <SiteNewsCard
          v-for="post in gridPosts"
          :key="post.slug"
          :post="post"
        />
      </div>
      <p
        v-else-if="!featured"
        class="mt-12 text-center text-[var(--site-muted)]"
      >
        {{ t('site.news_empty') }}
      </p>

      <!-- Pagination -->
      <div
        v-if="meta && meta.last_page > 1"
        class="mt-10 flex items-center justify-center gap-2"
      >
        <button
          type="button"
          class="site-btn-outline px-3 py-1.5 text-sm disabled:opacity-40"
          :disabled="page <= 1"
          @click="setQuery({ page: String(page - 1) })"
        >
          ← {{ t('site.news_prev') }}
        </button>
        <span class="text-sm text-[var(--site-muted)]">{{ page }} / {{ meta.last_page }}</span>
        <button
          type="button"
          class="site-btn-outline px-3 py-1.5 text-sm disabled:opacity-40"
          :disabled="page >= meta.last_page"
          @click="setQuery({ page: String(page + 1) })"
        >
          {{ t('site.news_next') }} →
        </button>
      </div>
    </div>
  </SiteShell>
</template>
